import { computed, makeObservable, observable } from 'mobx';
import { CrossSectionLogpoint } from './CrossSectionLogpoint';
import { CrossSectionAlignment } from './CrossSectionAlignment';
import { CrossSectionPipe } from './CrossSectionPipe';
import { CrossSectionModel } from './CrossSectionModel';
import { CrossSectionPointsMeasurement } from './CrossSectionPointsMeasurement';
import { Ajax2 } from '@shared/Ajax';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { CrossSectionUtils } from '@pages/cross-section/shared/CrossSectionUtils';
import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { latLonToEN } from '@openlayers/ol-utils';


interface Point {
    x: number;
    y: number;
    id?: number;
    color?: string;
}

interface Alignment {
    id: number;
    modelId: number;
    point: Point;
    color: string;
    props: {
        name: string;
    };
    intersectionStation: number;
}

interface Border {
    point: Point;
    color: string;
}

interface Pipe {
    point: Point;
    modelId: number;
    name: string;
    props: {
        elevType?: string;
        pipeNetType?: string;
        diameter?: string;
        thickness?: string;
    };
}

interface Logpoint {
    id: number;
    x: number;
    y: number;
    z: number;
    modelId: number;
    pointName?: string;
    number?: string;
    code?: string;
    type?: string;
    tol: number;
    modelDeltaZ?: number;
    tolerance: {
        upperLimit?: number;
        lowerLimit?: number;
    };
}

interface ICrossSectionResponse {
    freeCrossSection: boolean;
    width: number;
    height: number;
    logpointDelta: number;
    borders: Border[];
    customBorders: Border[];
    pipes: Pipe[];
    logpoints: Logpoint[];
    models: ICrossSectionModel[];
    terrainModels: ICrossSectionModel[];
    alignment: Alignment;
    backgroundAlignments: Alignment[];
    stationEquation: string;
    station: number;
    bounds: {
        valid: boolean;
        xmin: number;
        xmax: number;
        ymin: number;
        ymax: number;
        xcenter: number;
        ycenter: number;
    };
    gridCoordiantes: any | null;
    coordinates: {
        width: number;
        coords1: {
            n: number;
            e: number;
            z: number;
        },
        coords2: {
            n: number;
            e: number;
            z: number;
        },
        center: {
            n: number;
            e: number;
            z: number;
        }
    };
}


// data set on init to define what is loaded on scene
export interface ICrossSectionLoadSelection {
    alignmentId: number;
    alignmentIndex: number;
    folderId?: number;
    includeModelIds?: number[];
    selectedLogpointId?: number;
    selectedPipeId?: number;
    leftBorder?: number;
    rightBorder?: number;
}

export class CrossSectionStore {
    @observable.ref csCanvas: CrossSectionCanvas = null;
    @observable.ref loadSelection: ICrossSectionLoadSelection = null;
    @observable.ref latestServerData: ICrossSectionResponse = null;

    @observable isLoading = false;
    @observable isLoadingTree = true;
    @observable isCreatingPdf = false;

    @observable hiddenPipeIds: number[] = [];
    @observable hiddenLogpointIds: number[] = [];
    @observable hiddenModelIds: Array<string | number> = [];
    @observable showMobileModelsList = false;
    @observable showMobileMoreToolbar = false;

    modelNamesMap: Record<number, string>;
    currentInfoItem: string = null;

    // distance measurements
    clickedVertex0: ICrossSectionVertex = null;
    clickedVertex1: ICrossSectionVertex = null;
    clickedPoints: ICrossSectionVertex[] = [];

    @computed
    get getModelsControls() {
        if (!this.latestServerData) {
            return [];
        }
        return CrossSectionUtils.getModelsControls(this.latestServerData.models, this.latestServerData.logpoints, this.latestServerData.pipes, this.modelNamesMap);
    }

    readonly freeCrossSectionCoords = {
        lat1: null,
        lat2: null,
        lon1: null,
        lon2: null
    };

    @computed get isFreeCrossSection() {
        return this.freeCrossSectionCoords.lat1 !== null && this.freeCrossSectionCoords.lat2 !== null &&
            this.freeCrossSectionCoords.lon1 !== null && this.freeCrossSectionCoords.lon2 !== null;
    }

    @observable settings = {
        station: 0,
        step: 10,
        searchDisplacement: kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5,
        deltaZFilter: 0
    };

    constructor(private treeData?: ITreeData, private view?: CrossSectionViewState) {
        this.localStorageLoad();
        makeObservable(this);
        this.fetchInitialData();
    }

    destroy() {
        if (this.csCanvas) {
            this.csCanvas.destroy();
        }
    }

    setCanvasEl = async (canvasWrapperEl: HTMLDivElement, loadSelection: ICrossSectionLoadSelection) => {
        if (this.csCanvas) {
            return;
        }
        try {
            console.log('setCanvasEl:', canvasWrapperEl);
            this.csCanvas = new CrossSectionCanvas({
                zoomToCursorCorrection: this.zoomToCursorCorrection,
                view: this.view
            });
            await this.csCanvas.init({
                canvasWrapperEl,
                onViewChanged: this.drawAll
            });
            this.localStorageSave();
            await this.fetchDrawing(loadSelection);
        } catch (e) {
            console.log('e:', e);
            Notify.error('Failed to initialize cross-section scene');
        }
    }

    fetchInitialData = () => {
        this.isLoadingTree = true;
        ProjectCodesService.init();

        FileTreeService.fetchFileTree().then(td => {
            const namesMap = {}
            FileTreeService.eachPostOrder(td, 0, node => {
                if (node.models) {
                    node.models.forEach(m => {
                        namesMap[m.id] = m.name;
                    })
                }
            })
            this.modelNamesMap = namesMap;
        }).catch(Notify.error).finally(() => this.isLoadingTree = false)
    }

    fetchDrawing = async (loadSelection?: ICrossSectionLoadSelection, resetZoom = false) => {
        this.loadSelection = loadSelection || this.loadSelection;
        this.isLoading = true;

        let url: string = null;
        let data: any = null;

        const commonData = {
            projectId: kuura.getProjectId(),
            alignmentId: this.loadSelection.alignmentId,
            alignmentIndex: this.loadSelection.alignmentIndex,
            w: this.csCanvas.widthMetersInitial ?? kuura.getCrossSectionSettings()?.crossSectionWidth ?? 60,
            logpointDelta: this.settings.searchDisplacement,
            folderId: this.loadSelection.folderId,
            includeModelIds: this.loadSelection.includeModelIds?.join(',')
        }

        if (this.isFreeCrossSection) {
            if (this.csCanvas.widthMetersInitial === 0) {
                const [n1, e1] = latLonToEN(this.freeCrossSectionCoords.lat1, this.freeCrossSectionCoords.lon1);
                const [n2, e2] = latLonToEN(this.freeCrossSectionCoords.lat2, this.freeCrossSectionCoords.lon2);
                const dx = Math.abs(n1 - n2);
                const dy = Math.abs(e1 - e2);
                this.csCanvas.widthMeters = Math.ceil((Math.sqrt(dx * dx + dy * dy)));
                this.csCanvas.widthMetersInitial = this.csCanvas.widthMeters;
            }
            url = "free_cross_section.json";
            data = {
                ...commonData,
                w: this.csCanvas.widthMeters,
                lat1: this.freeCrossSectionCoords.lat1,
                lon1: this.freeCrossSectionCoords.lon1,
                lat2: this.freeCrossSectionCoords.lat2,
                lon2: this.freeCrossSectionCoords.lon2
            };
        } else {
            url = "cross_section.json";
            data = {
                ...commonData,
                station: this.settings.station,
            };
        }

        Ajax2({
            url,
            data,
            timeout: 5 * 60 * 1000
        }).then((resp: { crossSection: ICrossSectionResponse }) => {
            resp.crossSection.models.forEach(model => {
                model.lines.forEach((line, i) => {
                    line.id = model.modelId + '_' + i
                })
            })
            if (resetZoom) {
                this.csCanvas.panX = 0;
                this.csCanvas.panY = 0;
                this.csCanvas.widthMeters = 0;
            }

            this.csCanvas.lowestPoint = Math.floor(resp.crossSection.bounds.ymin);
            this.csCanvas.widthMetersInitial = parseInt(resp.crossSection.width.toFixed(0));
            if (this.csCanvas.widthMeters === 0) {
                this.csCanvas.widthMeters = this.csCanvas.widthMetersInitial;
            }
            this.settings.station = parseFloat(resp.crossSection.station.toFixed(2));
            this.settings.searchDisplacement = resp.crossSection.logpointDelta;
            this.latestServerData = resp.crossSection;

            this.clearAllTools();
            this.drawAll();
        }).catch(e => {
            //showErrors(data);
            Notify.error('Failed to load Cross Section');
            console.log('e:', e);
        }).finally(() => {
            this.isLoading = false;
        })
    }

    localStorageSave = () => {
        localStorage.setItem("cross-section-window-settings", JSON.stringify(this.settings));
    }

    localStorageLoad = () => {
        try {
            const settings = JSON.parse(localStorage.getItem("cross-section-window-settings"));
            if (settings) {
                this.settings.step = settings.step ?? 10;
                this.settings.searchDisplacement = settings.searchDisplacement ?? (kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5);
                this.settings.deltaZFilter = settings.deltaZFilter ?? 0;
            }
        } catch {}
    }

    drawAll = () => {
        //console.time('drawAll')
        const isInitialDraw = !this.latestServerData;

        const data: ICrossSectionResponse = this.latestServerData;
        if (!data) {
            console.log('drawAll - no data, returning');
            return;
        }

        this.csCanvas.clearStage();
        if (isInitialDraw) {
            this.panToAlignment(data);
        }
        this.csCanvas.drawGrid();
        //this.csCanvas.drawDebug();

        if (data.alignment) {
            this.drawAlignment(data.alignment, false);
        }

        if (!this.isFreeCrossSection && data.station >= 0) {
            this.csCanvas.drawHeader(data.alignment.props.name, data.stationEquation ? data.stationEquation : data.station.toFixed(2) + "m");
        }

        for (let alignment of (data.backgroundAlignments || [])) {
            this.drawAlignment(alignment, true);
        }

        for (let border of data.borders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        for (let border of data.customBorders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        for (let pipe of data.pipes) {
            pipe.name = this.modelNamesMap[pipe.modelId];
            CrossSectionPipe.drawPipe({
                id: 'pipe_' + pipe.modelId,
                pipe,
                csCanvas: this.csCanvas,
                visible: !this.hiddenPipeIds.includes(pipe.modelId),
                onClickCallback: (pipe, x, y) => {
                    this.currentInfoItem = 'pipe';
                    this.drawMeasurement(pipe, x, y, 11)
                }
            })
        }

        for (let model of data.models) {
            this.drawModel(model, 'model');
        }

        for (let model of data.terrainModels) {
            this.drawModel(model, 'terrainModel');
        }

        this.csCanvas.drawDistanceTool();
        this.csCanvas.drawAreaTool();

        // redraw measurements
        if (this.clickedPoints.length) {
            const center = this.csCanvas.getCenter();
            this.clickedVertex0 = null;
            const canvasOptions = this.csCanvas.canvasOptions
            const list = [...this.clickedPoints];
            this.clickedPoints = [];
            list.forEach(point => {
                let x = center + (point.point.x * this.csCanvas.canvasOptions.x_scale);
                let y = (this.csCanvas.height - canvasOptions.bottom_margin) + this.csCanvas.lowestPoint * canvasOptions.y_scale - (point.point.y * canvasOptions.y_scale) + this.csCanvas.panY;
                this.drawMeasurement(point.point, x, y, point.markerSize);
            })
        }

        for (let logpoint of data.logpoints) {
            const modelId = logpoint.modelId ?? -1;

            if (this.hiddenLogpointIds.includes(modelId)) {
                continue;
            }

            if (this.settings.deltaZFilter != null && logpoint.modelDeltaZ != null) {
                if (Math.abs(logpoint.modelDeltaZ) < this.settings.deltaZFilter) {
                    continue
                }
            }

            CrossSectionLogpoint.drawLogpoint({
                id: 'model_' + modelId,
                logpoint,
                isHighlighted: logpoint.id === this.loadSelection?.selectedLogpointId,
                csCanvas: this.csCanvas,
                onClickCallback: (logpoint, x, y) => {
                    this.drawMeasurement(logpoint, x, y, 11);
                    this.currentInfoItem = 'logpoint_' + logpoint.id;
                }
            })
        }

        //console.timeEnd('drawAll')
    }

    drawAlignment = (alignment: any, isBackground: boolean) => {
        alignment.name = this.modelNamesMap[alignment.id ?? alignment.modelId];
        CrossSectionAlignment.drawAlignment({
            alignment,
            isBackground,
            csCanvas: this.csCanvas,
            onClickCallback: (alignment, x, y) => {
                console.log('alignment:', alignment);
                this.drawMeasurement(alignment, x, y, 11)
            }
        });
    }

    drawModel = (model: any, type: 'model' | 'terrainModel') => {
        if (this.hiddenModelIds.includes(model.modelId) || model.empty) {
            return;
        }

        model.name = this.modelNamesMap[model.id ?? model.modelId];
        CrossSectionModel.drawModel({
            model,
            type,
            csCanvas: this.csCanvas,
            hiddenLineIds: this.hiddenModelIds,
            onClickCallback: (point, x, y) => {
                this.drawMeasurement(point, x, y, 7);
                this.currentInfoItem = 'vertex';
            }
        });
    }

    panToAlignment(crossSection: ICrossSectionResponse) {
        let alignment: any;
        if (crossSection.station >= 0) {
            alignment = crossSection.alignment;
        } else if (crossSection.backgroundAlignments.length > 0) {
            alignment = crossSection.backgroundAlignments[0];
        } else {
            return;
        }

        if (alignment.point.y !== 0) {
            let targetStationY = this.csCanvas.height / 3;

            const newPanY = targetStationY - ((this.csCanvas.height - this.csCanvas.canvasOptions.bottom_margin) +
                this.csCanvas.lowestPoint * this.csCanvas.canvasOptions.y_scale -
                alignment.point.y * this.csCanvas.canvasOptions.y_scale);

            if (newPanY && !isNaN(newPanY)) {
                this.csCanvas.panY = newPanY;
            }
        }
        this.csCanvas.initialPanY = this.csCanvas.panY;
    }

    drawMeasurement = (point: any, x: number, y: number, clickMarkerSize: number) => {
        const pt = {
            point: point.point ? { ... point.point } : { ...point },
            markerSize: clickMarkerSize,
            x,
            y
        }
        this.csCanvas.clearTooltips();
        this.clickedPoints.push(pt);
        CrossSectionPointsMeasurement.drawMeasurement(pt, x, y, this, this.csCanvas.measureTooltipLayer, this.csCanvas.tooltipLayer);
    }

    stationIncrease = () => {
        this.settings.station = this.settings.station + this.settings.step;
        this.fetchDrawing();
    }

    stationDecrease = () => {
        if (this.settings.station > 0) {
            this.settings.station = Math.max(0, this.settings.station - this.settings.step);
            this.fetchDrawing();
        }
    }

    createPdf = async () => {
        this.isCreatingPdf = true;
        const filename = this.latestServerData.station.toFixed(2) + ".pdf";
        await CrossSectionUtils.createPdf(filename, this.csCanvas.stage, this.getModelsControls);
        this.isCreatingPdf = false;
    }

    clearAllTools = () => {
        this.clickedPoints = [];
        this.clickedVertex0 = null;
        this.clickedVertex1 = null;
        if (this.csCanvas?.stage) {
            this.csCanvas.clearAllTools();
        }
    }

    zoomToCursorCorrection = () => {
        const canvasOptions = this.csCanvas.canvasOptions;
        const lowestPoint = this.csCanvas.lowestPoint;
        const mouseX = this.csCanvas.mouseX;
        const mouseY = this.csCanvas.mouseY;

        const pointX = (this.csCanvas.mouseX - this.csCanvas.getCenter()) / this.csCanvas.oldXScale;
        this.csCanvas.panX =  Math.round(mouseX - this.csCanvas.width / 2 - pointX * canvasOptions.x_scale);

        const pointY = ((this.csCanvas.height - canvasOptions.bottom_margin) + lowestPoint * this.csCanvas.oldYScale + this.csCanvas.panY - mouseY) / this.csCanvas.oldYScale;
        this.csCanvas.panY = mouseY + (pointY * canvasOptions.y_scale) - lowestPoint * canvasOptions.y_scale - (this.csCanvas.height - canvasOptions.bottom_margin);
    }

    getState = () => {
        if (!this.csCanvas) {
            return null;
        }

        return {
            loadSelection: this.loadSelection,
            station: this.settings.station > 0 ? this.settings.station : null,
            step: this.settings.step,
            searchDisplacement: this.settings.searchDisplacement,
            deltaZFilter: this.settings.deltaZFilter,
            view: this.csCanvas.getViewState(),
            ...this.freeCrossSectionCoords
        }
    }
}
